/**
 * WhyRooflyzerPage
 * /why-rooflyzer
 *
 * Real differentiators, updated comparison table (Rooflyzer vs JobNimbus vs AccuLynx vs BuilderTREND),
 * AI-Ready vs AI-Enabled, flexibility examples, and promises.
 */

'use client';

import { useReducedMotion, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Link } from 'react-router-dom';
import { AnimatedSection, StaggerContainer, StaggerItem } from '@/components/marketing';
import { getAuthLink } from '@/lib/links';
import { useSanityPage } from '@/lib/sanity.hooks';
import { useTranslation } from '../hooks/useTranslation';
import {
  ArrowRight,
  LockOpen,
  Database,
  Lightning,
  ArrowsClockwise,
  Download,
  Link as LinkIcon,
  Gear,
  CheckCircle,
  Target,
  ThumbsUp,
  Brain,
  X,
  Minus,
  Check,
  ChatText,
} from '@phosphor-icons/react';

const competitors = ['Rooflyzer OS', 'JobNimbus', 'AccuLynx', 'BuilderTREND'];

const comparisonRows = [
  {
    feature: 'Lead-to-contract automation',
    rooflyzer: 'included',
    jobnimbus: 'partial',
    acculynx: 'partial',
    buildertrend: 'limited',
    highlight: true,
  },
  {
    feature: 'Built-in marketing automation (email/SMS)',
    rooflyzer: 'included',
    jobnimbus: 'limited',
    acculynx: 'limited',
    buildertrend: 'limited',
    highlight: true,
  },
  {
    feature: 'Storm intelligence & territory targeting',
    rooflyzer: 'included',
    jobnimbus: 'limited',
    acculynx: 'limited',
    buildertrend: 'none',
    highlight: true,
  },
  {
    feature: 'Instant estimate widget for website',
    rooflyzer: 'included',
    jobnimbus: 'none',
    acculynx: 'third-party',
    buildertrend: 'none',
    highlight: true,
  },
  {
    feature: 'AI-ready data structure',
    rooflyzer: 'included',
    jobnimbus: 'none',
    acculynx: 'none',
    buildertrend: 'none',
    highlight: true,
  },
  {
    feature: 'Satellite roof measurements',
    rooflyzer: 'included',
    jobnimbus: 'third-party',
    acculynx: 'third-party',
    buildertrend: 'none',
    highlight: true,
  },
  {
    feature: 'Unlimited invoicing',
    rooflyzer: 'included',
    jobnimbus: 'addon',
    acculynx: 'addon',
    buildertrend: 'addon',
    highlight: false,
  },
  {
    feature: 'QuickBooks + Stripe integration',
    rooflyzer: 'included',
    jobnimbus: 'partial',
    acculynx: 'partial',
    buildertrend: 'partial',
    highlight: false,
  },
  {
    feature: 'White-label estimate widget',
    rooflyzer: 'included',
    jobnimbus: 'none',
    acculynx: 'none',
    buildertrend: 'none',
    highlight: false,
  },
  {
    feature: 'Data export anytime',
    rooflyzer: 'included',
    jobnimbus: 'partial',
    acculynx: 'partial',
    buildertrend: 'partial',
    highlight: false,
  },
  {
    feature: 'Starting price per user',
    rooflyzer: '$0',
    jobnimbus: '$45+',
    acculynx: '$200+',
    buildertrend: '$99+',
    highlight: true,
  },
];

const differentiators = [
  {
    icon: LockOpen,
    color: '#10B981',
    title: 'No restrictions',
    description:
      'Integrate anything. Import and export freely. No artificial limits on connections or how you use your data.',
    businessValue:
      'Connect the tools you already use. Reduce tool switching. Centralize data without vendor lock-in.',
  },
  {
    icon: Database,
    color: '#38BDF8',
    title: 'Your data, always',
    description:
      'Export your data anytime. No lock-in. No hostage situations. Your business data belongs to you.',
    businessValue:
      'Back up, analyze, or migrate your data whenever you want. Complete ownership from day one.',
  },
  {
    icon: ArrowsClockwise,
    color: '#10B981',
    title: 'Grows with you',
    description:
      'Start with our free tier. Add users and features when you are ready. The same platform works for a solo roofer or a 50-person operation.',
    businessValue:
      'Start free, scale as you grow. No need to switch platforms as your revenue increases.',
  },
  {
    icon: LinkIcon,
    color: '#38BDF8',
    title: 'Works with your tools',
    description:
      'Use SumoQuote for quotes, QuickBooks for accounting, or CompanyCam for photos if you want. Rooflyzer connects them instead of forcing you to replace them.',
    businessValue:
      'Use your preferred tools while centralizing data. Reduce duplicate data entry and keep your workflow.',
  },
];

const flexibilityExamples = [
  {
    scenario: 'Want invoicing handled by SumoQuote?',
    solution:
      'Integrate SumoQuote and route that module through them while keeping the job record in Rooflyzer.',
    icon: Gear,
  },
  {
    scenario: 'Prefer QuickBooks for accounting?',
    solution:
      'One-click OAuth sync. Update finances in QuickBooks, see profitability in Rooflyzer.',
    icon: ArrowsClockwise,
  },
  {
    scenario: 'Use EagleView for measurements?',
    solution:
      'Import the measurement PDF. Smart mapping fills fields and links the report to the estimate.',
    icon: Download,
  },
  {
    scenario: 'Already have a VOIP provider?',
    solution: 'Plug in your own API keys. Keep your existing numbers and call workflows.',
    icon: Lightning,
  },
];

const promises = [
  'No artificial feature gates to force upgrades',
  'No charging for integrations that should be standard',
  'No holding your data hostage if you want to leave',
  'Only charge for features that cost us money to provide',
];

/* ─── SEO ─────────────────────────────────────────────────────────── */
const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Why Rooflyzer',
      item: 'https://rooflyzer.pro/why-rooflyzer',
    },
  ],
};

function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'included') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium">
        <Check className="w-3 h-3" weight="bold" />
        Included
      </span>
    );
  }
  if (status === 'none') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 text-white/40 text-xs">
        <X className="w-3 h-3" weight="bold" />
        None
      </span>
    );
  }
  if (status === 'partial') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 text-white/50 text-xs">
        <Minus className="w-3 h-3" weight="bold" />
        Partial
      </span>
    );
  }
  if (status === 'third-party') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 text-white/50 text-xs">
        <Minus className="w-3 h-3" weight="bold" />
        Add-on
      </span>
    );
  }
  if (status === 'addon') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 text-white/50 text-xs">
        <Minus className="w-3 h-3" weight="bold" />
        Add-on
      </span>
    );
  }
  if (status === 'limited') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 text-white/50 text-xs">
        <Minus className="w-3 h-3" weight="bold" />
        Limited
      </span>
    );
  }
  return <span className="text-white/60 text-xs">{status}</span>;
}

/* ─────────────────────────────────────────────────────────────────── */

export const WhyRooflyzerPage = () => {
  const { t } = useTranslation();
  const { data: cmsData } = useSanityPage('why-rooflyzer');

  const pageTitle =
    cmsData?.seo?.metaTitle || 'Why Rooflyzer OS — Built Different for Roofing Contractors';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    cmsData?.description ||
    'See why roofing contractors choose Rooflyzer over generic CRMs. Purpose-built data model, AI-ready architecture, and roofing-specific workflows.';
  const heroTitle =
    cmsData?.title ||
    t('why.hero.titleStart', 'The CRM that actually') +
      ' ' +
      t('why.hero.titleHighlight', 'gets roofing');
  const heroSubtitle =
    cmsData?.description ||
    t(
      'why.hero.subtitle',
      "We didn't build another generic CRM and put a roof icon on it. Every feature exists because real contractors asked for it, and every data point is structured so your marketing actually works."
    );

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/why-rooflyzer"
        keywords="why rooflyzer, roofing software comparison, best roofing CRM, AI ready roofing software"
        ogImage={cmsData?.seo?.ogImage?.asset?.url || '/social-card.svg'}
        schema={breadcrumbSchema}
      />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-20 px-4 sm:px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-6">
              <Target className="w-4 h-4 text-emerald-400" weight="fill" />
              Why choose us
            </div>

            <h1 className="hero-h1 font-bold text-white mb-5">
              {cmsData?.title ? (
                heroTitle
              ) : (
                <>
                  {t('why.hero.titleStart', 'The CRM that actually')}{' '}
                  <span className="text-white">{t('why.hero.titleHighlight', 'gets roofing')}</span>
                </>
              )}
            </h1>

            <p className="hero-lead text-white/65 mb-4 max-w-2xl mx-auto">{heroSubtitle}</p>
            <p className="body-lg text-white/50 max-w-2xl mx-auto mb-8">
              {t('why.hero.startedAs', 'We started as')}{' '}
              <a
                href="https://rogue-move.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:underline font-semibold"
              >
                {t('why.hero.rogueMoveAgency', 'Rogue Move Digital Agency')}
              </a>
              {t(
                'why.hero.startedAsSuffix',
                ', helping roofing contractors grow through data-driven marketing. We discovered a critical gap: the tools contractors needed did not exist. So we built them, with complete data visibility from first click to final payment.'
              )}
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/features">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 rounded-lg shadow-sm"
                >
                  {t('why.hero.seeAllFeatures', 'See all features')}
                  <ArrowRight className="ml-2 size-5" />
                </Button>
              </Link>
              <Link to="/compare">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 px-8 rounded-lg"
                >
                  {t('why.hero.compareCompetitors', 'Compare competitors')}
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Differentiators Grid ───────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-4">
              <Brain className="w-4 h-4 text-emerald-400" weight="fill" />
              What makes us different
            </div>
            <h2 className="section-h2 font-bold text-white mb-3">
              {t('why.differentiators.title', 'A fundamentally different approach')}
            </h2>
            <p className="body-lg text-white/55 max-w-2xl mx-auto">
              {t(
                'why.differentiators.subtitle',
                'Built by marketers who understand the complete customer journey. Every feature is designed with data visibility in mind.'
              )}
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, i) => (
              <StaggerItem key={i}>
                <div className="bg-slate-900/60 rounded-3xl p-8 border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:-translate-y-1 h-full">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-slate-800 border border-slate-700/80">
                    <item.icon className="w-7 h-7" weight="fill" style={{ color: item.color }} />
                  </div>
                  <h3 className="section-h3 font-bold text-white mb-3">{item.title}</h3>
                  <p className="body-lg text-white/60 leading-relaxed mb-3">{item.description}</p>
                  {item.businessValue && (
                    <div className="bg-slate-800/60 rounded-xl p-3.5 mt-3 border border-slate-700/60">
                      <p className="text-slate-300 text-sm font-medium leading-relaxed">
                        {item.businessValue}
                      </p>
                    </div>
                  )}
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── Flexibility Section ────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-4">
              <Gear className="w-4 h-4 text-emerald-400" weight="fill" />
              Flexibility
            </div>
            <h2 className="section-h2 font-bold text-white mb-3">Use what works for you</h2>
            <p className="body-lg text-white/55 max-w-2xl mx-auto">
              We don't force you to use every module. Integrate the tools you already love.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-2 gap-5">
            {flexibilityExamples.map((example, i) => (
              <StaggerItem key={i}>
                <div className="bg-slate-900/60 rounded-2xl p-5 flex items-start gap-4 border border-slate-800 hover:border-slate-700 transition-all duration-300 h-full">
                  <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center flex-shrink-0 text-emerald-400">
                    <example.icon className="w-5 h-5" weight="fill" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm mb-1">{example.scenario}</p>
                    <p className="text-white/55 text-sm leading-relaxed">{example.solution}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── Comparison Table ───────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-4">
              <ThumbsUp className="w-4 h-4 text-emerald-400" weight="fill" />
              Honest comparison
            </div>
            <h2 className="section-h2 font-bold text-white mb-3">How we stack up</h2>
            <p className="body-lg text-white/55">
              Rooflyzer OS vs JobNimbus, AccuLynx, and BuilderTREND.
            </p>
          </AnimatedSection>

          <div className="overflow-x-auto rounded-2xl bg-slate-900/60 border border-slate-800">
            <table className="w-full text-left min-w-[700px]">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="px-6 py-4 text-slate-400 font-medium text-sm">Feature</th>
                  {competitors.map((name) => (
                    <th
                      key={name}
                      className={`px-4 py-4 text-center text-sm font-semibold ${name === 'Rooflyzer OS' ? 'text-white' : 'text-slate-400'}`}
                    >
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr
                    key={row.feature}
                    className={`border-b border-slate-800/50 transition-colors hover:bg-slate-800/30 ${
                      row.highlight ? 'bg-white/[0.02]' : ''
                    }`}
                  >
                    <td
                      className={`px-6 py-3 text-sm ${row.highlight ? 'text-white font-medium' : 'text-slate-300'}`}
                    >
                      {row.feature}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={row.rooflyzer} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={row.jobnimbus} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={row.acculynx} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={row.buildertrend} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-white/35 text-xs text-center mt-4">
            Comparison based on publicly available pricing and feature lists as of mid-2026.
          </p>
        </div>
      </section>

      {/* ── AI-Ready vs AI-Enabled ─────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-4">
              <Brain className="w-4 h-4 text-emerald-400" weight="fill" />
              AI-Ready vs AI-Enabled
            </div>
            <h2 className="section-h2 font-bold text-white mb-3">The difference matters</h2>
            <p className="body-lg text-white/55 max-w-2xl mx-auto">
              AI-enabled tools add chatbots on top of messy data. AI-ready tools capture clean data
              first.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="bg-slate-900/40 rounded-2xl p-8 border border-slate-800/80 h-full">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-slate-800/80 border border-slate-700/60 flex items-center justify-center">
                    <ChatText className="w-5 h-5 text-slate-400" weight="fill" />
                  </div>
                  <h3 className="section-h3 font-bold text-white">AI-Enabled</h3>
                </div>
                <ul className="space-y-3 text-slate-400 text-sm">
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" weight="bold" />
                    Bolted-on AI chatbot that answers generic questions
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" weight="bold" />
                    Data lives in disconnected fields and spreadsheets
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" weight="bold" />
                    No structured attribution from marketing to revenue
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="bg-slate-900/80 rounded-2xl p-8 border border-emerald-500/30 h-full shadow-lg shadow-emerald-950/20">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-emerald-400" weight="fill" />
                  </div>
                  <h3 className="section-h3 font-bold text-white">AI-Ready</h3>
                </div>
                <ul className="space-y-3 text-slate-300 text-sm">
                  <li className="flex items-start gap-2">
                    <Check
                      className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0"
                      weight="bold"
                    />
                    Lead scoring and anomaly detection live today
                  </li>
                  <li className="flex items-start gap-2">
                    <Check
                      className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0"
                      weight="bold"
                    />
                    Every touchpoint stored in a unified data model
                  </li>
                  <li className="flex items-start gap-2">
                    <Check
                      className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0"
                      weight="bold"
                    />
                    Exportable, structured data ready for future AI tools
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Promises ─────────────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="section-h2 font-bold text-white mb-3">What we promise</h2>
            <p className="body-lg text-white/55">
              No marketing fluff. Four commitments we actually keep.
            </p>
          </AnimatedSection>

          <StaggerContainer className="space-y-4">
            {promises.map((promise, i) => (
              <StaggerItem key={i}>
                <div className="flex items-center gap-4 bg-slate-900/60 rounded-xl p-5 border border-slate-800">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 text-emerald-400">
                    <CheckCircle className="w-4 h-4" weight="fill" />
                  </div>
                  <p className="text-white font-medium text-sm sm:text-base">{promise}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────── */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <AnimatedSection>
            <div className="bg-slate-900/80 rounded-3xl p-10 md:p-12 border border-slate-800">
              <h2 className="section-h2 font-bold text-white mb-4">
                {t('why.cta.title', 'See the difference for yourself')}
              </h2>
              <p className="text-white/55 body-lg mb-8">
                {t(
                  'why.cta.subtitle',
                  'Book a personalized demo and we will walk through your workflow, not a generic slide deck.'
                )}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/demo">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 rounded-lg shadow-sm"
                  >
                    {t('why.cta.bookDemo', 'Book a demo')}
                    <ArrowRight className="ml-2 size-5" />
                  </Button>
                </Link>
                <a
                  href={getAuthLink('/register')}
                  className="inline-flex items-center justify-center w-full sm:w-auto border border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 rounded-lg px-8 py-3 text-sm font-medium transition-colors"
                >
                  {t('why.cta.startFree', 'Start free')}
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};
